import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import { validation } from '@shopmy/universal';

import './BrandRecipientsModal.scss';
import { getBrandNotificationRecipients, saveBrandNotificationRecipients } from '../../api/brands';

// Edits a brand's notification recipients. The brand's own member accounts are always notified and shown
// read-only; the extra addresses below are the editable list. Callers slot context-specific buttons
// through `renderActions` — the tracking-health detail passes its "send test email" in this way.
const BrandRecipientsModal = props => {
  const { Brand_id, onClose, renderActions } = props;
  const rowId = useRef(0);
  const makeRow = (value = '') => ({ id: rowId.current++, value });
  const [members, setMembers] = useState([]);
  const [extras, setExtras] = useState(() => [makeRow()]);
  const [loadState, setLoadState] = useState('loading'); // loading, loaded, failed
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Saving replaces the stored list wholesale, so it stays off until the current list has actually
  // loaded — a save after a failed load would write this modal's empty default over real recipients.
  useEffect(() => {
    const loadRecipients = async () => {
      setLoadState('loading');
      try {
        const { members: brandMembers, extraEmails } = await getBrandNotificationRecipients(Brand_id);
        setMembers(brandMembers || []);
        setExtras(extraEmails?.length ? extraEmails.map(email => makeRow(email)) : [makeRow()]);
        setLoadState('loaded');
      } catch (error) {
        cogoToast.error(error || 'Could not load recipients.');
        setLoadState('failed');
      }
    };
    loadRecipients();
  }, [Brand_id, loadAttempt]);
  const hasLoaded = loadState === 'loaded';

  const updateExtra = (id, value) => setExtras(prev => prev.map(row => (row.id === id ? { ...row, value } : row)));
  const addExtra = () => setExtras(prev => [...prev, makeRow()]);
  const removeExtra = id => setExtras(prev => (prev.length === 1 ? [makeRow()] : prev.filter(row => row.id !== id)));

  const cleanedExtras = extras.map(row => row.value.trim()).filter(Boolean);
  const hasInvalidExtra = cleanedExtras.some(email => !validation.isValidEmail(email));

  const saveRecipients = async () => {
    if (isSaving || hasInvalidExtra || !hasLoaded) return;
    setIsSaving(true);
    try {
      await saveBrandNotificationRecipients(Brand_id, cleanedExtras.join(','));
      cogoToast.success('Recipients updated.');
      onClose();
    } catch (error) {
      cogoToast.error(error || 'Could not save recipients.');
    }
    setIsSaving(false);
  };

  return (
    <div className='brand-recipients-modal-outer' onClick={onClose}>
      <div className='brand-recipients-modal-inner' onClick={e => e.stopPropagation()}>
        <div className='brand-recipients-modal-header'>
          <span className='brand-recipients-modal-title'>Notification recipients</span>
          <span className='brand-recipients-modal-note'>
            The brand&apos;s own accounts are always notified. Extra addresses receive all of this brand&apos;s notifications, not only tracking
            alerts.
          </span>
        </div>

        {loadState === 'loading' && <div className='brand-recipients-modal-loading'>Loading…</div>}
        {loadState === 'failed' && (
          <div className='brand-recipients-modal-load-failed'>
            <span>We could not load this brand&apos;s current recipients. Saving stays off until they load, so nothing can be overwritten.</span>
            <button type='button' className='brand-recipients-modal-retry' onClick={() => setLoadAttempt(attempt => attempt + 1)}>
              Retry
            </button>
          </div>
        )}
        {hasLoaded && (
          <>
            <div className='brand-recipients-modal-section'>
              <span className='brand-recipients-modal-section-label'>Brand accounts · always notified</span>
              {members.length ? (
                members.map(member => (
                  <input
                    key={member.email}
                    className='brand-recipients-modal-input is-readonly'
                    type='text'
                    value={member.name ? `${member.name} · ${member.email}` : member.email}
                    disabled
                  />
                ))
              ) : (
                <span className='brand-recipients-modal-empty'>No brand accounts — only the extra addresses below will be notified.</span>
              )}
            </div>

            <div className='brand-recipients-modal-section'>
              <span className='brand-recipients-modal-section-label'>Extra recipients</span>
              {extras.map(row => {
                const showInvalid = row.value.trim() && !validation.isValidEmail(row.value.trim());
                return (
                  <div key={row.id} className='brand-recipients-modal-extra-row'>
                    <input
                      className={cn('brand-recipients-modal-input', { 'is-invalid': showInvalid })}
                      type='text'
                      value={row.value}
                      placeholder='name@example.com'
                      onChange={e => updateExtra(row.id, e.target.value)}
                    />
                    <button type='button' className='brand-recipients-modal-remove' onClick={() => removeExtra(row.id)}>
                      Remove
                    </button>
                  </div>
                );
              })}
              <button type='button' className='brand-recipients-modal-add' onClick={addExtra}>
                + Add recipient
              </button>
            </div>
          </>
        )}

        <div className='brand-recipients-modal-actions'>
          <button type='button' className='brand-recipients-modal-save' onClick={saveRecipients} disabled={isSaving || !hasLoaded || hasInvalidExtra}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          {renderActions && renderActions({ Brand_id })}
          <button type='button' className='brand-recipients-modal-close' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

BrandRecipientsModal.propTypes = {
  Brand_id: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  renderActions: PropTypes.func
};

export default BrandRecipientsModal;
